// =============================================================================
// VenueSpectator.js
// /var/www/binko-frontend/src/pages/VenueSpectator.js
//
// Tournament spectator view — fullscreen, TV/projector optimised.
// Requires venue admin login (binko_venue_token in localStorage).
// Open in a second tab alongside /venue/dashboard.
//
// Polls:
//   GET /api/venue/me          — once on mount, for venue name + logo_url
//   GET /api/tournament/state  — every 3s, for live phase data
//     (venue_id read from JWT via req.venueId server-side — no param needed
//      because the state endpoint is called from venue token auth below)
//
// NOTE: /api/tournament/state is a public endpoint that takes ?venue_id.
// We extract venue_id from the stored JWT payload client-side (no verification,
// same pattern as VenueAuthGuard) and pass it as a query param.
// =============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, LinearProgress, Chip, Button } from '@mui/material';
import { C } from '../theme';
import { IconTrophy, IconCorrect, IconNoAnswer } from '../BinkoIcons';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  return String(Math.floor(s / 60)) + ':' + String(s % 60).padStart(2, '0');
}

// NOT a security boundary — decodes stored JWT client-side for convenience only.
// Server always re-validates the token on every authenticated request.
function getVenueIdFromStoredToken() {
  try {
    const token = localStorage.getItem('binko_venue_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.venueId || null;
  } catch {
    return null;
  }
}

function venueHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + (localStorage.getItem('binko_venue_token') || ''),
  };
}

function getScheduleMessage(upcoming) {
  if (!upcoming) return { line1: 'Tournaments from 6 PM tonight', line2: 'Every 30 min · Signup opens 5 min before' };
  const start   = new Date(upcoming.scheduled_start);
  const now     = new Date();
  const timeStr = start.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  const isToday = start.toDateString() === now.toDateString();
  if (isToday) {
    return { line1: 'Next tournament at ' + timeStr, line2: 'Signup opens 5 minutes before · Play for free' };
  }
  return { line1: 'Next tournament tomorrow at ' + timeStr, line2: 'Rounds every 30 min from 6:00 PM' };
}

function computeRanks(lb) {
  const ranks = [];
  let rank = 1;
  for (let i = 0; i < lb.length; i++) {
    if (i > 0 && lb[i].total_score < lb[i - 1].total_score) rank = i + 1;
    ranks.push(rank);
  }
  return ranks;
}

function rankLabel(rank) {
  if (rank === 1) return '\uD83E\uDD47';
  if (rank === 2) return '\uD83E\uDD48';
  if (rank === 3) return '\uD83E\uDD49';
  return '#' + rank;
}

const PHASE_LABEL = {
  ROUND_1: 'Round 1', ROUND_2: 'Round 2',
  SEMI: 'Semi-final', FINAL: 'Final', SUDDEN_DEATH: 'Sudden Death',
};

const PHASE_Q_COUNT = {
  ROUND_1: 10, ROUND_2: 8, SEMI: 4, FINAL: 3, SUDDEN_DEATH: 3,
};

const RESULT_PHASES = new Set([
  'ROUND_1_RESULT','ROUND_2_RESULT','SEMI_RESULT','FINAL_RESULT','SUDDEN_DEATH_RESULT'
]);
const BREAK_PHASES = new Set(['BREAK_1','BREAK_2','BREAK_3']);
const LEADERBOARD_PHASES = new Set([
  'ROUND_1_LEADERBOARD','ROUND_2_LEADERBOARD','SEMI_LEADERBOARD',
  'FINAL_LEADERBOARD','SUDDEN_DEATH_LEADERBOARD'
]);
const QUESTION_PHASES = new Set(['ROUND_1','ROUND_2','SEMI','FINAL','SUDDEN_DEATH']);

// ---------------------------------------------------------------------------
// Venue logo watermark — bottom-right corner
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Binko brand watermark — bottom-left
// ---------------------------------------------------------------------------
function BinkoWatermark() {
  return (
    <Box sx={{ position: 'fixed', bottom: 24, left: 28, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
      <Typography sx={{ color: C.textDisabled, fontSize: '0.55rem', letterSpacing: 1.5, textTransform: 'uppercase' }}>
        Powered by
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <img src="/logo.png" alt="Binko" style={{ height: 22, width: 'auto' }}
          onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
        <Typography sx={{ display: 'none', color: C.yellow, fontWeight: 900, fontSize: '1.2rem', letterSpacing: 3, lineHeight: 1 }}>
          BINKO
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// IDLE screen — "Live Trivia is on here tonight"
// ---------------------------------------------------------------------------
function IdleScreen({ state, venueName }) {
  const msg = getScheduleMessage(state.upcoming);
  return (
    <Box sx={SS.screen}>
      <IconTrophy size={52} style={{ marginBottom: 16 }} />
      <Typography sx={SS.bigTitle}>Live Trivia is on here tonight</Typography>
      <Typography sx={{ color: C.teal, fontWeight: 600, fontSize: '1.4rem', mt: 1 }}>
        {venueName}
      </Typography>
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography sx={SS.subtitle}>{msg.line1}</Typography>
        <Typography sx={{ color: C.textDisabled, fontSize: '1rem', mt: 1, textAlign: 'center' }}>
          {msg.line2}
        </Typography>
      </Box>
      <Box sx={{
        mt: 3, background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.3)',
        borderRadius: 3, px: 5, py: 2, textAlign: 'center',
      }}>
        <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.1rem' }}>
          Open Binko to join
        </Typography>
        <Typography sx={{ color: C.textMuted, fontSize: '0.9rem', mt: 0.5 }}>
          app.binko.me
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SIGNUP screen
// ---------------------------------------------------------------------------
function SignupScreen({ state }) {
  const [secs, setSecs] = useState(state.seconds_to_start);

  useEffect(() => { setSecs(state.seconds_to_start); }, [state.seconds_to_start]);
  useEffect(() => {
    const t = setInterval(() => setSecs(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <Box sx={SS.screen}>
      <IconTrophy size={52} style={{ marginBottom: 16 }} />
      <Typography sx={SS.bigTitle}>Tournament starting soon</Typography>
      <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '5rem', my: 2, fontVariantNumeric: 'tabular-nums' }}>
        {secs > 0 ? fmt(secs) : 'Starting…'}
      </Typography>
      <Typography sx={SS.subtitle}>
        {state.player_count} player{state.player_count !== 1 ? 's' : ''} joined
      </Typography>
      <Box sx={{
        mt: 4, background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.3)',
        borderRadius: 3, px: 5, py: 2, textAlign: 'center',
      }}>
        <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.2rem' }}>
          Open Binko to join → app.binko.me
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// QUESTION screen — options shown, not clickable
// ---------------------------------------------------------------------------
function QuestionScreen({ state }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 250);
    return () => clearInterval(t);
  }, []);

  const q = state.current_question;
  if (!q) return <Box sx={SS.screen}><Typography sx={SS.subtitle}>Loading question…</Typography></Box>;

  const phaseStart    = state.phase_start_at;
  const phaseDuration = state.question_duration_ms || 11000;

  function progressPct() {
    if (!phaseStart) return 100;
    const elapsed = Date.now() - phaseStart;
    return Math.max(0, Math.min(100, 100 - (elapsed / phaseDuration) * 100));
  }
  function timeRemaining() {
    if (!phaseStart) return 0;
    return Math.max(0, Math.ceil((phaseStart + phaseDuration - Date.now()) / 1000));
  }

  const tRem    = timeRemaining();
  const pPct    = progressPct();
  const urgent  = tRem <= 3;
  const phLabel = PHASE_LABEL[state.phase] || state.phase;
  const qNum    = (state.current_question_index ?? 0) + 1;
  const qTotal  = PHASE_Q_COUNT[state.phase] || '?';

  return (
    <Box sx={{ ...SS.screen, justifyContent: 'flex-start', pt: '6vh' }}>
      {/* Meta row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, width: '100%', maxWidth: 900 }}>
        <Chip label={phLabel} sx={{ background: 'rgba(251,188,21,0.15)', color: C.yellow, fontWeight: 700, fontSize: '0.95rem', height: 32 }} />
        <Typography sx={{ color: C.textDisabled, fontSize: '1rem' }}>
          Q{qNum} of {qTotal}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ color: urgent ? C.orange : C.textPrimary, fontWeight: 700, fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums' }}>
          {tRem}s
        </Typography>
      </Box>

      {/* Timer bar */}
      <LinearProgress
        variant="determinate"
        value={pPct}
        sx={{
          width: '100%', maxWidth: 900, height: 10, borderRadius: 5,
          mb: 4, background: C.bgElevated,
          '& .MuiLinearProgress-bar': { background: urgent ? C.orange : C.yellow, transition: 'none' }
        }}
      />

      {/* Question */}
      <Typography sx={{
        color: C.textPrimary, fontWeight: 700, fontSize: '2rem',
        textAlign: 'center', mb: 5, lineHeight: 1.4, maxWidth: 860,
      }}>
        {q.question}
      </Typography>

      {/* Options — display only, no interaction */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%', maxWidth: 900 }}>
        {['A','B','C','D'].map(letter => (
          <Box key={letter} sx={{
            background: C.bgCard, border: '1px solid ' + C.border,
            borderRadius: 3, px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '1.3rem', minWidth: 28 }}>
              {letter}
            </Typography>
            <Typography sx={{ color: C.textPrimary, fontSize: '1.15rem', lineHeight: 1.4 }}>
              {q.options[letter]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RESULT screen — correct answer revealed
// ---------------------------------------------------------------------------
function ResultScreen({ state }) {
  const q = state.current_question;
  if (!q) return null;

  return (
    <Box sx={{ ...SS.screen, justifyContent: 'flex-start', pt: '8vh' }}>
      <Box sx={{ mb: 2 }}>{q.correct ? <IconCorrect size={48} /> : <IconNoAnswer size={48} />}</Box>
      <Typography sx={SS.bigTitle}>Correct Answer</Typography>

      <Box sx={{ width: '100%', maxWidth: 800, mt: 3 }}>
        {['A','B','C','D'].map(letter => {
          const isCorrect = letter === q.correct;
          return (
            <Box key={letter} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              borderRadius: 3, px: 3, py: 2, mb: 1.5,
              background: isCorrect ? 'rgba(34,197,94,0.12)' : C.bgCard,
              border: '1px solid ' + (isCorrect ? C.success : C.border),
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: isCorrect ? C.success : C.textDisabled, minWidth: 28 }}>
                {letter}
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', color: isCorrect ? C.success : C.textMuted, fontWeight: isCorrect ? 700 : 400 }}>
                {q.options[letter]}
              </Typography>
              {isCorrect && <Typography sx={{ ml: 'auto', fontSize: '1.2rem' }}>✓</Typography>}
            </Box>
          );
        })}
      </Box>

      {state.correct_count !== undefined && (
        <Typography sx={{ color: C.textMuted, fontSize: '1rem', mt: 2 }}>
          {state.correct_count} of {state.total_answered} players got it right
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LEADERBOARD screen
// ---------------------------------------------------------------------------
function LeaderboardScreen({ state }) {
  const lb        = state.leaderboard || [];
  const ranks     = computeRanks(lb);
  const basePhase = state.phase.replace('_LEADERBOARD', '');
  const phLabel   = PHASE_LABEL[basePhase] || 'Round';
  const qNum      = (state.current_question_index ?? 0) + 1;
  const qTotal    = PHASE_Q_COUNT[basePhase] || '?';

  return (
    <Box sx={{ ...SS.screen, justifyContent: 'flex-start', pt: '6vh' }}>
      <Typography sx={SS.bigTitle}>{phLabel} · Q{qNum} of {qTotal}</Typography>
      <Typography sx={{ color: C.textMuted, fontSize: '1rem', mb: 4, mt: 0.5 }}>Standings after this question</Typography>

      <Box sx={{ width: '100%', maxWidth: 700 }}>
        {lb.slice(0, 8).map((p, i) => {
          const eliminated = p.status === 'ELIMINATED';
          return (
            <Box key={p.display_name || i} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              background: C.bgCard, borderRadius: 3, px: 3, py: 2, mb: 1.5,
              opacity: eliminated ? 0.38 : 1,
              border: '1px solid ' + C.border,
            }}>
              <Typography sx={{ fontSize: '1.5rem', minWidth: 44, textAlign: 'center' }}>
                {rankLabel(ranks[i])}
              </Typography>
              <Typography sx={{ flexGrow: 1, color: C.textPrimary, fontSize: '1.2rem', fontWeight: 600 }}>
                {p.display_name}
              </Typography>
              {eliminated && (
                <Chip label="Out" size="small" sx={{ fontSize: '0.75rem', background: 'rgba(254,114,16,0.12)', color: C.orange }} />
              )}
              <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '1.2rem' }}>
                {p.total_score} pts
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BREAK screen
// ---------------------------------------------------------------------------
function BreakScreen({ state }) {
  const lb    = state.leaderboard || [];
  const ranks = computeRanks(lb);
  const up    = state.upcoming_phase;

  const BREAK_LABEL = { BREAK_1: 'Round 2', BREAK_2: 'Semi-final', BREAK_3: 'Final' };
  const nextRound = BREAK_LABEL[state.phase] || 'Next Round';

  return (
    <Box sx={{ ...SS.screen, flexDirection: 'row', alignItems: 'flex-start', pt: '6vh', gap: 6, justifyContent: 'center' }}>

      {/* Left: break info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 280 }}>
        <Typography sx={{ fontSize: '3rem', mb: 2 }}>⏸️</Typography>
        <Typography sx={SS.bigTitle}>Break</Typography>
        <Typography sx={{ color: C.teal, fontWeight: 700, fontSize: '1.2rem', mt: 1 }}>
          {nextRound} up next
        </Typography>
        {state.break_seconds_remaining > 0 && (
          <Typography sx={{ color: C.textDisabled, fontSize: '1rem', mt: 2 }}>
            Resuming in {state.break_seconds_remaining}s
          </Typography>
        )}
        <Box sx={{
          mt: 4, background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.3)',
          borderRadius: 3, px: 4, py: 2, textAlign: 'center',
        }}>
          <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1rem' }}>
            Join now → app.binko.me
          </Typography>
        </Box>
      </Box>

      {/* Right: leaderboard */}
      <Box sx={{ minWidth: 380 }}>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.85rem', mb: 2, textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
          Current Standings
        </Typography>
        {lb.slice(0, 6).map((p, i) => {
          const eliminated = p.status === 'ELIMINATED';
          return (
            <Box key={p.display_name || i} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              background: C.bgCard, borderRadius: 3, px: 3, py: 1.75, mb: 1.25,
              opacity: eliminated ? 0.38 : 1,
              border: '1px solid ' + C.border,
            }}>
              <Typography sx={{ fontSize: '1.3rem', minWidth: 38 }}>{rankLabel(ranks[i])}</Typography>
              <Typography sx={{ flexGrow: 1, color: C.textPrimary, fontSize: '1.1rem', fontWeight: 600 }}>
                {p.display_name}
              </Typography>
              {eliminated && (
                <Chip label="Out" size="small" sx={{ fontSize: '0.7rem', background: 'rgba(254,114,16,0.12)', color: C.orange }} />
              )}
              <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '1.1rem' }}>
                {p.total_score}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// WINNER screen
// ---------------------------------------------------------------------------
function WinnerScreen({ state }) {
  const lb      = state.leaderboard || [];
  const winners = lb.filter(p => p.is_winner);
  const top3    = lb.slice(0, 3);
  const ranks   = computeRanks(top3);

  return (
    <Box sx={SS.screen}>
      <IconTrophy size={52} style={{ marginBottom: 16 }} />
      <Typography sx={SS.bigTitle}>
        {winners.length > 1 ? 'Joint Winners!' : state.via_sudden_death ? 'Sudden Death Winner!' : 'Winner!'}
      </Typography>
      {winners.map(w => (
        <Typography key={w.user_id} sx={{ color: C.yellow, fontWeight: 800, fontSize: '2.2rem', mt: 0.5 }}>
          {w.display_name}
        </Typography>
      ))}
      <Typography sx={{ color: C.textMuted, fontSize: '1rem', mt: 1 }}>
        {state.is_official ? (winners.length > 1 ? 'Both earn a trophy 🏆' : 'Earns a trophy 🏆') : 'Unofficial game — no trophies awarded'}
      </Typography>

      <Box sx={{ mt: 4, width: '100%', maxWidth: 600 }}>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.8rem', mb: 2, textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
          Final Standings
        </Typography>
        {top3.map((p, i) => (
          <Box key={p.user_id || i} sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            background: p.is_winner ? 'rgba(251,188,21,0.08)' : C.bgCard,
            border: '1px solid ' + (p.is_winner ? 'rgba(251,188,21,0.3)' : C.border),
            borderRadius: 3, px: 3, py: 2, mb: 1.5,
          }}>
            <Typography sx={{ fontSize: '1.8rem', minWidth: 48 }}>{rankLabel(ranks[i])}</Typography>
            <Typography sx={{ flexGrow: 1, color: C.textPrimary, fontSize: '1.3rem', fontWeight: 700 }}>
              {p.display_name}
            </Typography>
            {p.is_winner && <IconTrophy size={16} style={{ marginRight: 4 }} />}
            <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '1.3rem' }}>
              {p.total_score} pts
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{
        mt: 4, background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.3)',
        borderRadius: 3, px: 5, py: 2, textAlign: 'center',
      }}>
        <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.1rem' }}>
          Next round: app.binko.me
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CANCELLED screen
// ---------------------------------------------------------------------------
function CancelledScreen({ state }) {
  const msg = getScheduleMessage(state.upcoming);
  return (
    <Box sx={SS.screen}>
      <Typography sx={{ fontSize: '3rem', mb: 2 }}>😔</Typography>
      <Typography sx={SS.bigTitle}>Not enough players</Typography>
      <Typography sx={SS.subtitle}>Need at least 2 players to start</Typography>
      <Typography sx={{ color: C.textMuted, fontSize: '1rem', mt: 3 }}>{msg.line1}</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// TickerTrack — continuous scrolling orange bar
// ---------------------------------------------------------------------------
// TickerItems — built dynamically from trivia spectator state
// Falls back to mechanic anchors when no live data available
function buildTickerItems(trivia) {
  const msgs = [];
  if (trivia) {
    const lb = trivia.leaderboard || [];
    const playerCount = trivia.active_player_count || 0;
    const top = lb[0];
    if (playerCount > 0)
      msgs.push(playerCount + ' people playing trivia right now');
    if (top && top.alias && top.score_display)
      msgs.push('Top scorer: ' + top.alias + ' — ' + top.score_display + ' pts');
    if (lb.length >= 3 && lb[2].score_display > 0)
      msgs.push(lb.length + ' players on the leaderboard tonight');
  }
  // Mechanic anchors — always present
  msgs.push('Play trivia · meet people between rounds');
  msgs.push("Score 100+ pts to win tonight's offer");
  msgs.push('Check in on Binko · app.binko.me');
  msgs.push('Like someone? Start a chat on Binko');
  msgs.push('Tournament starts tonight · check in to enter');
  msgs.push('Binko · Play · Meet · Win');
  return msgs;
}

// Inject ticker keyframe once
if (typeof document !== 'undefined' && !document.getElementById('binko-ticker-kf')) {
  const s = document.createElement('style');
  s.id = 'binko-ticker-kf';
  s.textContent = '@keyframes ticker { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }';
  document.head.appendChild(s);
}

function TickerTrack({ trivia }) {
  const dot = <Box component="span" sx={{ display: 'inline-block', width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)', mx: '36px', verticalAlign: 'middle', flexShrink: 0 }} />;
  const base = buildTickerItems(trivia);
  const items = [...base, ...base]; // doubled for seamless loop
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
      animation: 'ticker 30s linear infinite',
      willChange: 'transform',
    }}>
      {items.map((item, i) => (
        <Box key={i} component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography component="span" sx={{
            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.8px', color: '#fff', whiteSpace: 'nowrap',
          }}>{item}</Typography>
          {dot}
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SplitFlapPanel — alternates trivia top3 and tournament winners every 6s
// ---------------------------------------------------------------------------
function SplitFlapPanel({ trivia, todayWinners }) {
  const [mode, setMode] = useState(0); // 0 = trivia top3, 1 = tournament winners
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setFlipping(true);
      setTimeout(() => {
        setMode(m => (m + 1) % 2);
        setFlipping(false);
      }, 200);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const top3   = trivia?.daily_top3 || [];
  const slots  = todayWinners || [];

  // Build 5 rows for each dataset
  const triviaRows = [0,1,2,3,4].map(i => {
    const p = top3[i];
    return p
      ? { medal: i===0?'🥇':i===1?'🥈':'🥉', name: p.alias, score: p.total_score + ' pts', dim: false }
      : { medal: '—', name: '', score: '', dim: true };
  });

  // Only show slots that have a declared winner — hide cancelled/upcoming/not_started
  const completedSlots = (todayWinners || []).filter(s => s.status === 'completed' && s.winner_alias);
  const tournRows = [0,1,2,3,4].map(i => {
    const s = completedSlots[i];
    if (!s) return { medal: '', time: '', name: '', score: '', dim: true };
    return { medal: '🥇', time: s.scheduled_start, name: s.winner_alias, score: s.score ? s.score + ' pts' : '', dim: false };
  });

  const rows    = mode === 0 ? triviaRows : tournRows;
  const title   = mode === 0 ? "TONIGHT'S TRIVIA · TOP 3" : 'TOURNAMENT WINNERS';
  const showTime = mode === 1;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '8px', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textDisabled, flexGrow: 1 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: '4px' }}>
          {[0,1].map(i => (
            <Box key={i} sx={{ width: 5, height: 5, borderRadius: '50%', background: i === mode ? C.yellow : C.border, transition: 'background 0.3s' }} />
          ))}
        </Box>
      </Box>

      {/* Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
        {rows.map((row, i) => (
          <Box key={i} sx={{
            display: 'flex', alignItems: 'center', gap: '6px',
            opacity: flipping ? 0 : 1,
            transform: flipping ? 'rotateX(-90deg)' : 'rotateX(0deg)',
            transition: `opacity 0.18s ease ${i * 30}ms, transform 0.18s ease ${i * 30}ms`,
            transformOrigin: '50% 100%',
            minHeight: '22px',
          }}>
            <Typography sx={{ fontSize: '0.82rem', width: '18px', flexShrink: 0 }}>{row.medal}</Typography>
            {showTime && (
              <>
                <Typography sx={{ fontSize: '0.68rem', color: row.dim ? C.textDisabled : C.textMuted, width: '36px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {row.time}
                </Typography>
                <Box sx={{ width: '1px', height: '12px', background: C.border, flexShrink: 0 }} />
              </>
            )}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: row.dim ? 400 : 700, color: row.dim ? C.textDisabled : C.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.name}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: row.dim ? C.textDisabled : C.yellow, flexShrink: 0 }}>
              {row.score}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TournamentCountdownRow — side-by-side countdown + signup info boxes
// ---------------------------------------------------------------------------
function TournamentCountdownRow({ state }) {
  // Compute target time once when state changes — tick purely client-side
  const targetRef = useRef(null);
  useEffect(() => {
    const upcoming = state?.upcoming;
    const phase    = state?.phase;
    if (upcoming?.scheduled_start) {
      targetRef.current = new Date(upcoming.scheduled_start).getTime();
    } else if (phase === 'SIGNUP' && state?.seconds_to_start > 0) {
      targetRef.current = Date.now() + state.seconds_to_start * 1000;
    } else {
      targetRef.current = null;
    }
  }, [state?.upcoming?.scheduled_start, state?.phase]);

  const [cdSecs, setCdSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      if (targetRef.current) {
        setCdSecs(Math.max(0, Math.ceil((targetRef.current - Date.now()) / 1000)));
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const upcoming = state?.upcoming;
  const phase    = state?.phase;

  const p2 = n => String(n).padStart(2, '0');
  const h = Math.floor(cdSecs / 3600);
  const m = Math.floor((cdSecs % 3600) / 60);
  const s = cdSecs % 60;
  const cdLabel = h > 0 ? `${h}:${p2(m)}:${p2(s)}` : `${p2(m)}:${p2(s)}`;

  let signupLabel = '';
  if (upcoming?.signup_opens_at) {
    signupLabel = 'Signup at ' + new Date(upcoming.signup_opens_at)
      .toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  } else if (phase === 'SIGNUP') {
    signupLabel = 'Signup open now';
  }

  const playerCount = state?.player_count ?? 0;

  return (
    <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
      {/* Countdown box */}
      <Box sx={{
        flex: 1, background: 'rgba(0,0,0,0.4)',
        border: '1px solid rgba(251,188,21,0.22)',
        borderRadius: '8px', padding: '8px 12px',
      }}>
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: C.yellow, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
          {cdSecs > 0 ? cdLabel : phase === 'SIGNUP' ? 'Soon' : '--:--'}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, mt: '2px' }}>
          {phase === 'SIGNUP' ? 'Starting in' : 'Starts in'}
        </Typography>
      </Box>
      {/* Signup info box */}
      <Box sx={{
        flex: 1, background: 'rgba(255,255,255,0.03)',
        border: '1px solid ' + C.border,
        borderRadius: '8px', padding: '8px 12px',
      }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.3 }}>
          {signupLabel || 'From 6 PM nightly'}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: C.textMuted, mt: '3px', lineHeight: 1.4 }}>
          {playerCount > 0 ? `${playerCount} signed up` : 'Every 30 min'}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TriviaPanel — spectator trivia display (no interaction, display-only)
// ---------------------------------------------------------------------------
function TriviaPanel({ trivia }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 250);
    return () => clearInterval(t);
  }, []);

  // ── Helpers ──
  const headerRow = (label, right) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '0.85rem', letterSpacing: 2 }}>{label}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {right}
    </Box>
  );

  const optionsList = (q, showCorrect) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {['A','B','C','D'].map(letter => {
        const isCorrect = showCorrect && letter === q.correct;
        return (
          <Box key={letter} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            borderRadius: 2, px: 2, py: 1.2,
            background: isCorrect ? 'rgba(34,197,94,0.12)' : C.bgCard,
            border: '1px solid ' + (isCorrect ? C.success : C.border),
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: isCorrect ? C.success : C.yellow, minWidth: 22 }}>{letter}</Typography>
            <Typography sx={{ fontSize: '0.95rem', color: isCorrect ? C.success : C.textPrimary, fontWeight: isCorrect ? 700 : 400, lineHeight: 1.35 }}>
              {q.options[letter]}
            </Typography>
            {isCorrect && <Typography sx={{ ml: 'auto', fontSize: '1rem', color: C.success }}>✓</Typography>}
          </Box>
        );
      })}
    </Box>
  );

  const leaderboardPanel = (entries, players) => (
    <Box>
      {headerRow('LIVE TRIVIA · STANDINGS',
        <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem' }}>
          {playerCount} playing · app.binko.me
        </Typography>
      )}
      {(!entries || entries.length === 0) ? (
        <Typography sx={{ color: C.textMuted, fontSize: '0.9rem', textAlign: 'center', mt: 3 }}>
          No players yet · app.binko.me
        </Typography>
      ) : (
        <>
          {entries.filter(e => e.source === 'home').length > 0 && (
            <Box sx={{ height: '1px', background: C.border, mb: 1.5 }} />
          )}
          {entries.filter(e => e.source === 'home').map((e, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
              background: C.bgCard, border: '1px solid ' + C.border,
              borderRadius: 2, px: 2, py: 1, mb: 0.75 }}>
              <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.9rem', minWidth: 20 }}>#{i + 1}</Typography>
              <Typography sx={{ flexGrow: 1, color: C.textPrimary, fontSize: '0.95rem', fontWeight: 600 }}>{e.alias}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.9rem' }}>{e.score_display}</Typography>
                <Box sx={{ background: 'rgba(13,157,165,0.15)', border: '1px solid ' + C.teal, borderRadius: 1, px: 0.8, py: 0.2 }}>
                  <Typography sx={{ color: C.teal, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5 }}>HERE</Typography>
                </Box>
              </Box>
            </Box>
          ))}
          {entries.filter(e => e.source === 'away').length > 0 && (
            <>
              {entries.filter(e => e.source === 'away').map((e, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid ' + C.border,
                  borderRadius: 2, px: 2, py: 1, mb: 0.75, opacity: 0.8 }}>
                  <Typography sx={{ color: C.textDisabled, fontSize: '0.85rem', minWidth: 20 }}>-</Typography>
                  <Typography sx={{ flexGrow: 1, color: C.textMuted, fontSize: '0.9rem' }}>{e.alias}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>{e.score_display}</Typography>
                    <Box sx={{ background: 'rgba(251,188,21,0.08)', border: '1px solid rgba(251,188,21,0.25)', borderRadius: 1, px: 0.8, py: 0.2 }}>
                      <Typography sx={{ color: C.textDisabled, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5 }}>AWAY</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </Box>
  );

  // ── No data yet ──
  if (!trivia || trivia.phase === 'WAITING') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, px: 3, py: 4 }}>
        <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '1.2rem', letterSpacing: 2 }}>LIVE TRIVIA</Typography>
        <Typography sx={{ color: C.textMuted, fontSize: '1rem' }}>Game starting soon…</Typography>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.85rem' }}>Open Binko to join · app.binko.me</Typography>
      </Box>
    );
  }

  const phase = trivia.phase;
  const playerCount = trivia.active_player_count ?? 0;
  const qNum = trivia.current_round ?? 0;

  // ── Shared page wrapper — skipHeader=true for phases with their own title ──
  const pageWrap = (children, skipHeader = false) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', px: 3, pt: 3, pb: 3, height: '100%' }}>
      {!skipHeader && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '0.85rem', letterSpacing: 2 }}>
              LIVE TRIVIA
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem' }}>
              {playerCount} playing · app.binko.me
            </Typography>
          </Box>
          <Box sx={{ height: '1px', background: C.border, mb: 2 }} />
        </>
      )}
      {children}
    </Box>
  );

  // ── QUESTION phase ──
  if (phase === 'QUESTION' && trivia.question) {
    const q = trivia.question;
    const phaseDuration = trivia.phase_duration_ms || 11000;
    const phaseStart = trivia.phase_start_at;
    const elapsed = phaseStart ? Date.now() - phaseStart : 0;
    const tRem = Math.max(0, Math.ceil((phaseDuration - elapsed) / 1000));
    const pPct = phaseStart ? Math.max(0, Math.min(100, 100 - (elapsed / phaseDuration) * 100)) : 100;
    const urgent = tRem <= 3;
    return pageWrap(
      <>
        {/* Q label + timer on same line */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.textMuted, fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1 }}>
            Q{qNum}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ color: urgent ? C.orange : C.textPrimary, fontWeight: 700, fontSize: '1.1rem', fontVariantNumeric: 'tabular-nums' }}>
            {tRem}s
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={pPct}
          sx={{ height: 5, borderRadius: 3, mb: 2, background: C.bgElevated,
                '& .MuiLinearProgress-bar': { background: urgent ? C.orange : C.yellow, transition: 'none' } }} />
        <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.5, mb: 2.5 }}>
          {q.question}
        </Typography>
        {optionsList(q, false)}
      </>
    );
  }

  // ── RESULT phase — question frozen at top, answer revealed below ──
  if (phase === 'RESULT' && trivia.question) {
    const q = trivia.question;
    return pageWrap(
      <>
        {/* Timer frozen at 0 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.textMuted, fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1 }}>
            Q{qNum}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ color: C.textDisabled, fontWeight: 700, fontSize: '1.1rem', fontVariantNumeric: 'tabular-nums' }}>
            0s
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={0}
          sx={{ height: 5, borderRadius: 3, mb: 2, background: C.bgElevated,
                '& .MuiLinearProgress-bar': { background: C.border, transition: 'none' } }} />
        {/* Same question text — frozen */}
        <Typography sx={{ color: C.textMuted, fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.5, mb: 2.5 }}>
          {q.question}
        </Typography>
        {/* Answer revealed */}
        {optionsList(q, true)}
        {trivia.correct_count !== undefined && (
          <Box sx={{ mt: 2, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
            <Typography sx={{ color: C.success, fontSize: '0.8rem', fontWeight: 600 }}>
              {trivia.correct_count} of {trivia.total_answered} got it right
            </Typography>
          </Box>
        )}
      </>
    );
  }

  // ── LEADERBOARD phase ──
  if (phase === 'LEADERBOARD') {
    return pageWrap(
      trivia.leaderboard
        ? leaderboardPanel(trivia.leaderboard, playerCount)
        : <Typography sx={{ color: C.textMuted, textAlign: 'center', mt: 4 }}>Loading standings…</Typography>,
      true  // skipHeader — leaderboardPanel has its own title
    );
  }

  // ── BREAK phase — countdown + leaderboard ──
  if (phase === 'BREAK') {
    const phaseDuration = trivia.phase_duration_ms || 120000;
    const phaseStart = trivia.phase_start_at;
    const elapsed = phaseStart ? Date.now() - phaseStart : 0;
    const secsRem = Math.max(0, Math.ceil((phaseDuration - elapsed) / 1000));
    return pageWrap(
      <>
        {/* Same-position header as other phases but with BREAK label + countdown inline */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ color: C.yellow, fontWeight: 800, fontSize: '0.85rem', letterSpacing: 2 }}>
            LIVE TRIVIA · BREAK
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
            {fmt(secsRem)}
          </Typography>
        </Box>
        <Box sx={{ height: '1px', background: C.border, mb: 2 }} />
        {trivia.leaderboard
          ? leaderboardPanel(trivia.leaderboard, playerCount)
          : <Typography sx={{ color: C.textMuted, textAlign: 'center', mt: 3 }}>Loading standings…</Typography>
        }
      </>,
      true  // skipHeader
    );
  }

  // ── Fallback / IDLE with leaderboard ──
  const lb = trivia.leaderboard;
  if (lb) {
    return pageWrap(leaderboardPanel(lb, playerCount));
  }

  return pageWrap(
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2 }}>
      <Typography sx={{ color: C.textMuted, fontSize: '0.95rem' }}>
        {playerCount > 0 ? playerCount + ' players in game' : 'Open Binko to join · app.binko.me'}
      </Typography>
    </Box>
  );
}

// =============================================================================
// OVERLAY COMPONENTS
// Each is fully self-contained. To swap a design, edit only the component.
// The orchestrator in VenueSpectator() only flips showBumper / showWinner.
// =============================================================================

// -----------------------------------------------------------------------------
// BumperOverlay — 3s fullscreen brand bumper
// Props: onDone() — called after 3000ms
// To replace: rewrite only this function. Keep onDone() call at 3000ms.
// -----------------------------------------------------------------------------
function BumperOverlay({ onDone }) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - start) / 3000) * 100));
    }, 30);
    return () => clearInterval(tick);
  }, []);

  React.useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: '#0D0D0D',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: '16px',
    }}>
      <Typography sx={{ color: C.yellow, fontWeight: 900, fontSize: '62px', letterSpacing: '8px', lineHeight: 1 }}>
        BINKO
      </Typography>
      <Typography sx={{ color: C.textMuted, fontWeight: 600, fontSize: '1.1rem', letterSpacing: '3px', textTransform: 'uppercase' }}>
        Play · Meet · Win
      </Typography>
      <Box sx={{ width: 220, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 99, mt: '8px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', background: C.yellow, borderRadius: 99, width: progress + '%', transition: 'none' }} />
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// WinnerOverlay — 5s cycle-winner announcement with confetti
// Props: winner { alias, score }, offerText (string), onDone()
// To replace: rewrite only this function. Keep onDone() call at 5000ms.
// -----------------------------------------------------------------------------
function WinnerOverlay({ winner, offerText, onDone }) {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [onDone]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const COLORS = [C.yellow, C.teal, C.orange, '#fff'];
    const pieces = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height * -0.5,
      w: 7 + Math.random() * 7, h: 4 + Math.random() * 4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      vx: (Math.random() - 0.5) * 2.5,
      vy: 2.5 + Math.random() * 3.5,
      vr: (Math.random() - 0.5) * 0.12,
    }));
    let raf;
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
      });
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: '12px',
    }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase' }}>
          Cycle Winner
        </Typography>
        <Typography sx={{ color: C.yellow, fontWeight: 900, fontSize: '3rem', lineHeight: 1.1 }}>
          {winner?.alias || ''}
        </Typography>
        <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: '1.4rem' }}>
          {winner?.score ?? 0} pts
        </Typography>
        {offerText && (
          <Box sx={{ mt: '8px', background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.3)', borderRadius: '10px', px: '28px', py: '12px' }}>
            <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1rem' }}>
              🎁 {offerText}
            </Typography>
          </Box>
        )}
        <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem', mt: '4px' }}>
          Collect your 4-digit code at the bar
        </Typography>
      </Box>
    </Box>
  );
}

// Main component
// ---------------------------------------------------------------------------
// Carousel slide keyframes — injected once
if (typeof document !== 'undefined' && !document.getElementById('binko-car-kf')) {
  const s = document.createElement('style');
  s.id = 'binko-car-kf';
  s.textContent = `
    @keyframes carSlideInRight  { from { transform:translateX(100%); opacity:0 } to { transform:translateX(0); opacity:1 } }
    @keyframes carSlideOutLeft  { from { transform:translateX(0);    opacity:1 } to { transform:translateX(-100%); opacity:0 } }
    @keyframes carSlideInLeft   { from { transform:translateX(-100%);opacity:0 } to { transform:translateX(0); opacity:1 } }
    @keyframes carSlideOutRight { from { transform:translateX(0);    opacity:1 } to { transform:translateX(100%); opacity:0 } }
  `;
  document.head.appendChild(s);
}

export default function VenueSpectator() {
  const [state,      setState]      = useState(null);
  const [trivia,     setTrivia]     = useState(null);
  const [venue,      setVenue]      = useState(null);  // { name, logo_url }
  const [error,      setError]      = useState('');
  const [pollFails,  setPollFails]  = useState(0);
  const [carouselIdx,   setCarouselIdx]   = useState(0);
  const [carouselDir,   setCarouselDir]   = useState(1); // 1 = forward, -1 = backward
  const [offers,        setOffers]       = useState([]);
  const [todayWinners,  setTodayWinners]  = useState([]);
  const venueId   = getVenueIdFromStoredToken();
  const pollRef   = useRef(null);
  const triviaRef = useRef(null);

  // Overlay state — orchestrator flips these, overlays handle their own visuals
  const [showBumper,  setShowBumper]  = useState(false);
  const [showWinner,  setShowWinner]  = useState(false);
  const [cycleWinner, setCycleWinner] = useState(null); // { alias, score }
  const breakPhaseKeyRef = useRef(null); // prevents re-firing same BREAK cycle

  // Fetch venue info once on mount
  useEffect(() => {
    async function fetchVenue() {
      try {
        const res  = await fetch(API + '/api/venue/me', { headers: venueHeaders() });
        const data = await res.json();
        if (data.venue) setVenue(data.venue);
      } catch (_) {}
    }
    fetchVenue();
  }, []);

  // Fetch venue offers once on mount, refresh every 5 min
  useEffect(() => {
    async function fetchOffers() {
      try {
        const res  = await fetch(API + '/api/venue/offers?venue_id=' + encodeURIComponent(venueId));
        const data = await res.json();
        if (data.offers) setOffers(data.offers);
      } catch (_) {}
    }
    fetchOffers();
    const t = setInterval(fetchOffers, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [venueId]);

  // Fetch today's tournament winners every 60s
  useEffect(() => {
    if (!venueId) return;
    async function fetchWinners() {
      try {
        const res  = await fetch(API + '/api/tournament/today-winners?venue_id=' + encodeURIComponent(venueId));
        const data = await res.json();
        if (data.slots) setTodayWinners(data.slots);
      } catch (_) {}
    }
    fetchWinners();
    const t = setInterval(fetchWinners, 60000);
    return () => clearInterval(t);
  }, [venueId]);

  // Carousel auto-advance — simple forward loop, wraps at end
  useEffect(() => {
    const t = setInterval(() => setCarouselIdx(i => (i + 1) % 4), 4000);
    return () => clearInterval(t);
  }, []);

  // Poll tournament state every 3s
  const poll = useCallback(async () => {
    if (!venueId) return;
    try {
      const res  = await fetch(API + '/api/tournament/state?venue_id=' + encodeURIComponent(venueId));
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      setState(data);
      setPollFails(0);
      setError('');
    } catch (_) {
      setPollFails(f => {
        const next = f + 1;
        if (next >= 3) setError('Connection lost — retrying…');
        return next;
      });
    }
  }, [venueId]);

  // Poll trivia state every 2s (public endpoint, no auth)
  const pollTrivia = useCallback(async () => {
    if (!venueId) return;
    try {
      const res  = await fetch(API + '/api/trivia/spectator-state?venue_id=' + encodeURIComponent(venueId));
      if (!res.ok) return;
      const data = await res.json();
      setTrivia(data);
    } catch (_) {}
  }, [venueId]);

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  useEffect(() => {
    pollTrivia();
    triviaRef.current = setInterval(pollTrivia, 2000);
    return () => clearInterval(triviaRef.current);
  }, [pollTrivia]);

  // ── BREAK sub-sequence orchestrator ─────────────────────────────────────
  // Watches trivia phase. Fires once per BREAK cycle via phase_start_at key.
  // Guard: suppressed during any active tournament.
  // To change timing: edit the scheduleAt() calls only.
  useEffect(() => {
    if (!trivia || trivia.phase !== 'BREAK') return;

    const breakKey = trivia.phase_start_at;
    if (breakPhaseKeyRef.current === breakKey) return;
    breakPhaseKeyRef.current = breakKey;

    const elapsed = Date.now() - (trivia.phase_start_at || Date.now());

    function scheduleAt(targetMs, fn, windowMs = 8000) {
      const delay = targetMs - elapsed;
      // If already past target but still within window, fire immediately
      if (delay < 0 && elapsed < targetMs + windowMs) { fn(); return null; }
      if (delay < 0) return null;
      return setTimeout(fn, delay);
    }

    const homeEntries = (trivia.leaderboard || []).filter(e => e.source === 'home');
    const top = homeEntries[0];
    const winner = top && Number(top.score_display) >= 100
      ? { alias: top.alias, score: Number(top.score_display) }
      : null;

    const timers = [];
    let bumper1FiredAt = null;

    timers.push(scheduleAt(0, () => {
      bumper1FiredAt = Date.now();
      setShowBumper(true);
    }));

    timers.push(scheduleAt(3000, () => {
      // Guarantee bumper shows for at least 3s from when it actually fired
      const bumperElapsed = bumper1FiredAt ? Date.now() - bumper1FiredAt : 3000;
      const remaining = Math.max(0, 3000 - bumperElapsed);
      setTimeout(() => {
        setShowBumper(false);
        if (winner) { setCycleWinner(winner); setShowWinner(true); }
      }, remaining);
    }));

    timers.push(scheduleAt(8000,   () => setShowWinner(false)));
    timers.push(scheduleAt(107000, () => setShowBumper(true)));
    timers.push(scheduleAt(110000, () => setShowBumper(false)));

    return () => timers.forEach(t => t && clearTimeout(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trivia?.phase, trivia?.phase_start_at]);

  // No token — redirect to login
  if (!venueId) {
    window.location.href = '/venue/login';
    return null;
  }

  const venueName = venue ? venue.name : (localStorage.getItem('binko_venue_name') || '');
  const logoUrl   = venue ? venue.logo_url : null;

  // Loading (wait for tournament state; trivia loads separately)
  if (!state) {
    return (
      <Box sx={{ ...SS.root, alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '1.2rem', letterSpacing: 2 }}>
          BINKO
        </Typography>
        <Typography sx={{ color: C.textMuted, fontSize: '0.9rem', mt: 1 }}>
          Connecting…
        </Typography>
        <BinkoWatermark />
      </Box>
    );
  }

  const phase = state.phase;

  // Tournament is "inactive" when IDLE or CANCELLED — trivia fills full screen
  const tournamentActive = phase !== 'IDLE' && phase !== 'CANCELLED';

  let tournamentScreen = null;
  if (tournamentActive) {
    if (phase === 'SIGNUP') {
      tournamentScreen = <SignupScreen state={state} />;
    } else if (QUESTION_PHASES.has(phase)) {
      tournamentScreen = <QuestionScreen state={state} />;
    } else if (RESULT_PHASES.has(phase)) {
      tournamentScreen = <ResultScreen state={state} />;
    } else if (LEADERBOARD_PHASES.has(phase)) {
      tournamentScreen = <LeaderboardScreen state={state} />;
    } else if (BREAK_PHASES.has(phase)) {
      tournamentScreen = <BreakScreen state={state} />;
    } else if (phase === 'WINNER') {
      tournamentScreen = <WinnerScreen state={state} />;
    }
  }

  return (
    <Box sx={SS.root}>
      {/* Overlays — swap designs by editing BumperOverlay / WinnerOverlay above */}
      {showBumper && !tournamentActive && (
        <BumperOverlay onDone={() => setShowBumper(false)} />
      )}
      {showWinner && !tournamentActive && cycleWinner && (
        <WinnerOverlay
          winner={cycleWinner}
          offerText={offers.find(o => o.offer_type === 'trivia')?.offer_text || ''}
          onDone={() => setShowWinner(false)}
        />
      )}
      {/* Bottom-left — Venue name/logo (primary brand, their screen) */}
      <Box sx={{
        position: 'fixed', bottom: 38, left: 20, zIndex: 20,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px',
      }}>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.5rem', letterSpacing: 1.4, textTransform: 'uppercase', lineHeight: 1 }}>
          Tonight at
        </Typography>
        {logoUrl ? (
          <img src={logoUrl} alt={venueName} style={{ height: 44, width: 'auto', borderRadius: 6, opacity: 0.9 }} />
        ) : (
          <Typography sx={{ color: C.textPrimary, fontWeight: 900, fontSize: '1.3rem', letterSpacing: '-0.3px', lineHeight: 1 }}>
            {venueName}
          </Typography>
        )}
      </Box>

      {/* Bottom-right — Binko attribution (smaller, footnote position) */}
      <Box sx={{
        position: 'fixed', bottom: 38, right: 20, zIndex: 20,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px',
      }}>
        <Typography sx={{ color: C.textDisabled, fontSize: '0.5rem', letterSpacing: 1.4, textTransform: 'uppercase', lineHeight: 1 }}>
          Powered by
        </Typography>
        <img src="/logo.png" alt="Binko" style={{ height: 18, width: 'auto' }}
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
        <Typography sx={{ display: 'none', color: C.yellow, fontWeight: 900, fontSize: '0.9rem', letterSpacing: 3, lineHeight: 1 }}>
          BINKO
        </Typography>
      </Box>

      {/* Main content area — always 3 columns: trivia 50% | sb-l 25% | sb-r 25% */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', pb: '32px', height: '100vh' }}>

        {/* LEFT — trivia panel (50%) */}
        <Box sx={{
          width: '50%', borderRight: '1px solid ' + C.border,
          background: C.bgApp, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', minHeight: 0,
        }}>
          {tournamentActive
            ? <Box sx={{ flex: 1, overflow: 'auto', px: '3vw', pb: 4 }}>{tournamentScreen}</Box>
            : <TriviaPanel trivia={trivia} />
          }
        </Box>

        {/* RIGHT SIDEBAR (50%) */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>

          {/* sb-l — QR (top 50%) + Carousel (bottom 50%) — 25% screen */}
          <Box sx={{
            width: '50%', borderRight: '1px solid ' + C.border,
            display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0,
            background: C.bgApp,
          }}>
            {/* QR block */}
            <Box sx={{
              flex: '0 0 50%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '10px 12px 6px', background: C.bgApp, overflow: 'hidden',
            }}>
              <Box sx={{
                width: '100%', flex: 1, minHeight: 0,
                background: C.bgCard, borderRadius: '10px',
                border: '1px solid ' + C.teal,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '12px 14px', overflow: 'hidden',
              }}>
                <Box sx={{ background: '#fff', borderRadius: '8px', padding: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <img src="/QRCode.png" alt="Scan to join" style={{ width: 115, height: 115, display: 'block' }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: C.textPrimary, letterSpacing: '-0.3px', mb: '2px' }}>Scan to play</Typography>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.teal, letterSpacing: '0.3px', mb: '6px' }}>app.binko.me</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                    {[['PLAY', C.yellow], ['MEET', C.teal], ['REPEAT', C.orange]].map(([lbl, col]) => (
                      <Box key={lbl} sx={{ background: col + '18', border: '1px solid ' + col + '55', borderRadius: '99px', px: '8px', py: '2px' }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: col }}>{lbl}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
            {/* Carousel block */}
            {(() => {
              const triviaOffer = offers.find(o => o.offer_type === 'trivia');
              const tournOffer  = offers.find(o => o.offer_type === 'tournament');
              const slides = [
                {
                  accent: C.teal,
                  label: 'WIN AT TRIVIA',
                  icon: '🧠',
                  text: triviaOffer?.offer_text || 'Top scorer each round wins tonight\'s offer. Score 100+ pts to qualify.',
                },
                {
                  accent: C.yellow,
                  label: 'WIN THE TOURNAMENT',
                  icon: '🏆',
                  text: tournOffer?.offer_text || 'Tournament champion wins a special reward tonight. Join before it starts.',
                },
                {
                  accent: C.orange,
                  label: 'MEET THE ROOM',
                  icon: '👋',
                  text: 'Spot someone interesting? Wave at them on Binko — no awkwardness, just good energy.',
                },
                {
                  accent: C.textMuted,
                  label: 'GOOD TO KNOW',
                  icon: '📋',
                  text: 'Offers valid tonight only. Show your 4-digit win code at the bar. One redemption per win.',
                },
              ];
              const slide = slides[carouselIdx];
              return (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 12px 8px', minHeight: 0 }}>
                  {/* Pill dots */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: '6px', gap: '4px', alignItems: 'center' }}>
                    {slides.map((s, i) => (
                      <Box key={i} sx={{
                        width: i === carouselIdx ? '16px' : '5px', height: '5px',
                        borderRadius: i === carouselIdx ? '3px' : '50%',
                        background: i === carouselIdx ? slides[i].accent : 'rgba(255,255,255,0.15)',
                        transition: 'all 0.3s',
                      }} />
                    ))}
                  </Box>
                  {/* Sliding track */}
                  <Box sx={{
                    flex: 1, borderRadius: '10px',
                    border: '0.5px solid rgba(255,255,255,0.07)',
                    background: 'rgba(255,255,255,0.02)',
                    overflow: 'hidden', minHeight: 0,
                  }}>
                    <Box sx={{
                      display: 'flex',
                      transform: `translateX(-${carouselIdx * 100}%)`,
                      transition: 'transform 0.38s cubic-bezier(0.4,0,0.2,1)',
                      height: '100%',
                    }}>
                      {slides.map((sl, i) => (
                        <Box key={i} sx={{
                          minWidth: '100%', height: '100%',
                          display: 'flex', flexDirection: 'column',
                        }}>
                          {/* Header strip */}
                          <Box sx={{ flexShrink: 0, padding: '8px 14px 7px', borderBottom: '1px solid ' + C.border }}>
                            <Typography sx={{
                              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px',
                              textTransform: 'uppercase', color: sl.accent,
                            }}>{sl.label}</Typography>
                          </Box>
                          {/* Body */}
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 14px', gap: '8px' }}>
                            <Typography sx={{ fontSize: '1.8rem', lineHeight: 1 }}>{sl.icon}</Typography>
                            <Typography sx={{
                              fontSize: '0.76rem', color: C.textMuted, lineHeight: 1.55,
                              textAlign: 'center',
                            }}>{sl.text}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </Box>

          {/* sb-r — Tournament column — 25% screen */}
          <Box sx={{
            width: '50%', display: 'flex', flexDirection: 'column',
            overflow: 'hidden', minHeight: 0, background: C.bgApp,
          }}>
            {/* Header */}
            <Box sx={{
              flexShrink: 0, padding: '11px 18px 10px',
              background: 'rgba(251,188,21,0.025)',
              borderBottom: '1px solid rgba(251,188,21,0.14)',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: C.yellow, letterSpacing: 1, flexGrow: 1 }}>
                🏆 TOURNAMENT TONIGHT
              </Typography>
              {state?.upcoming && (
                <Box sx={{
                  background: 'rgba(251,188,21,0.1)', border: '1px solid rgba(251,188,21,0.25)',
                  borderRadius: '99px', px: '8px', py: '2px',
                }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: C.yellow }}>
                    {new Date(state.upcoming.scheduled_start).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase()}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Body */}
            <Box sx={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, overflow: 'hidden' }}>

              {/* Countdown + Signup row */}
              <TournamentCountdownRow state={state} />
              {/* Split-flap leaderboard */}
              <SplitFlapPanel trivia={trivia} todayWinners={todayWinners} />

            </Box>
          </Box>

        </Box>
      </Box>

      {/* Orange ticker bar — fixed bottom */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, height: '32px',
        background: C.orange, zIndex: 150,
        display: 'flex', alignItems: 'center', overflow: 'hidden',
      }}>
        <TickerTrack trivia={trivia} />
      </Box>

    </Box>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const SS = {
  root: {
    minHeight: '100vh',
    background: C.bgApp,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: '5vw', py: 1.5,
    borderBottom: '1px solid ' + C.border,
    background: C.bgElevated,
  },
  screen: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    minHeight: '80vh', width: '100%',
    textAlign: 'center',
  },
  bigTitle: {
    color: C.textPrimary, fontWeight: 800,
    fontSize: '2.4rem', textAlign: 'center', lineHeight: 1.2,
  },
  subtitle: {
    color: C.textMuted, fontSize: '1.2rem', textAlign: 'center',
  },
};
